import { getExceptionDetails, Select, variants } from '@uag/react-core';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCountries, useGetDoka360IsActiveInCountryHook } from 'api/queries/countries/countries';

type Props = {
    onCountryChanged: (countryId: string, isDoka360Available: boolean, countryCode: string) => void;
    initialCountryId?: string;
};

export const NewCustomerCountryField = ({ onCountryChanged: handleCountryChanged, initialCountryId = '' }: Props) => {
    const { t } = useTranslation();
    const { data: countriesResponse, isPending: areCountriesLoading } = useGetCountries();
    const getDoka360Availability = useGetDoka360IsActiveInCountryHook();
    const [countryId, setCountryId] = useState<string>(initialCountryId);
    const [availabilityError, setAvailabilityError] = useState<string | undefined>();
    const latestRequestId = useRef(0);

    const countries = useMemo(() => countriesResponse?.data || [], [countriesResponse?.data]);
    const sortedCountries = useMemo(
        () =>
            countries
                .map(({ id, name, countryCode }) => ({ value: id, label: name || countryCode }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [countries],
    );

    const handleChange = useCallback(
        async (selectedCountryId: string | null) => {
            const requestId = ++latestRequestId.current;
            setCountryId(selectedCountryId ?? '');
            setAvailabilityError(undefined);

            if (!selectedCountryId) {
                handleCountryChanged('', false, '');
                return;
            }

            const selectedCountry = countries.find((country) => country.id === selectedCountryId);
            if (!selectedCountry) {
                return;
            }

            try {
                const response = await getDoka360Availability(selectedCountry.countryCode);
                if (requestId !== latestRequestId.current) {
                    return;
                }
                handleCountryChanged(selectedCountryId, response.data, selectedCountry.countryCode);
            } catch (error) {
                if (requestId !== latestRequestId.current) {
                    return;
                }
                setAvailabilityError(getExceptionDetails(error)?.message);
                handleCountryChanged('', false, '');
            }
        },
        [countries, getDoka360Availability, handleCountryChanged],
    );

    return (
        <Select
            classNames={{ section: 'border-l-0' }}
            data={sortedCountries}
            disabled={areCountriesLoading || countries.length <= 0}
            error={availabilityError}
            label={t('country')}
            maxDropdownHeight={200}
            placeholder={t('selectPlaceholder', { label: t('country') })}
            value={countryId}
            variant={variants.textField.textDecoration}
            required
            searchable
            onChange={handleChange}
        />
    );
};
